import React, { useEffect, useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { Label } from './label';
import { Button } from './button';
import { composeValidators, required, validatePassword } from '../../helpers/form-validations';
import { EyeNoneIcon, EyeOpenIcon } from '@radix-ui/react-icons';
import PasswordStrengthMeter from '../login/PasswordStrengthMeter';
import { useDispatch, useSelector } from 'react-redux';
import { changePassword } from '../../utils/util';
import { loaderSet } from '../../context/actions/loaderAction';
import { alertDanger, alertSuccess } from '../../context/actions/toastAction';

const ResetPasswordDialog = ({ onClose }) => {
  const dialogRef = useRef(null);
  const oldPasswordInputRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [oldPassword,setOldPassword]=useState("")
  const [newPassword,setNewPassword]=useState("")
  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const userInfo=useSelector(state=>state.user)
  const dispatch=useDispatch()
  const onSubmit = async (values) => {
    dispatch(loaderSet(true))
    console.log("Reset Password Values:", values);

    const payload={
      email:userInfo && userInfo?.email,
      oldPassword:values.oldPassword,
      newPassword:values.newPassword
    }
    try {
      const response=await changePassword(payload)
      if(response.code===200){
        console.log("response handle submit change password",response)
        dispatch(alertSuccess("Successfully Changed Password"))
        onClose(); // Close dialog after submission
        dispatch(loaderSet(false))
        return 
      }
      if(response.code===400){
        dispatch(alertDanger("Incorrect old Password"))
        // onClose(); // Close dialog after submission
        dispatch(loaderSet(false))
      }
    } catch (error) {
      dispatch(loaderSet(false))
      dispatch(alertDanger("Change Password After Some Time"))
      console.log("response handle submit change password",error)
    }
    
  };

  useEffect(() => {
    if (!dialogRef.current) return;

    const container = dialogRef.current;
    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusable = container.querySelectorAll(focusableSelector);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (oldPasswordInputRef.current) {
      oldPasswordInputRef.current.focus();
    } else if (first) {
      first.focus();
    }

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab' || focusable.length === 0) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="presentation">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-password-title"
        className="bg-white p-8 rounded-md w-[90%] max-w-lg shadow-lg relative"
      >
      <h2 className="text-lg font-semibold mb-4">Reset Password</h2>
  <button 
    type='button' 
    onClick={onClose} 
    className="absolute top-6 right-6 h-11 w-11 rounded-md font-bold text-2xl text-red-600 focus-visible:ring-2 focus-visible:ring-[#0D215C]"
    aria-label="Close reset password dialog"
  >
   X
  </button>
        <h2 id="reset-password-title" className="sr-only">Reset Password</h2>
        <Form
          onSubmit={onSubmit}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex flex-col">
              <Field
  id="oldPassword"
  name="oldPassword"
  type={showPassword ? 'text' : 'password'}
  placeholder="*************"
  validate={composeValidators(required)}
  render={({ input, meta }) => {
    const showOldPasswordError = Boolean(meta?.error && (meta?.submitFailed || meta?.modified));

    return <div className="relative grid gap-2">
      <div className="flex items-center">
        <Label className="text-start text-[#0D215C] font-bold text-lg" htmlFor="password">Old Password</Label>
      </div>
      <input
        {...input}
        ref={oldPasswordInputRef}
        type={showPassword ? 'text' : 'password'}
        value={oldPassword}
        aria-invalid={showOldPasswordError ? 'true' : 'false'}
        aria-describedby={showOldPasswordError ? 'oldPassword-error' : undefined}
        onChange={(e) => {
          input.onChange(e); // Update form state
          setOldPassword(e.target.value); // Also update password state
        }}
        placeholder="*************"
      className="bg-white pl-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0D215C] h-9 w-full rounded-md text-base border border-[#3a5dd1] border-1 manrope-fontCss"
      />
      {showOldPasswordError && (
        <p id="oldPassword-error" className="text-red-600 text-sm text-left" role="alert" aria-live="assertive">&nbsp;{meta.error}</p>
      )}
      <button
        type="button"
        className="absolute h-11 top-10 right-0 flex items-center px-4 cursor-pointer rounded-md focus-visible:ring-2 focus-visible:ring-[#0D215C]"
        onClick={togglePasswordVisibility}
        aria-label={showPassword ? 'Hide old password' : 'Show old password'}
        aria-pressed={showPassword}
      >
        {showPassword ? (
          <EyeNoneIcon className="h-5 w-5 text-black" />
        ) : (
          <EyeOpenIcon className="h-5 w-5 text-black" />
        )}
      </button>
    </div>;
  }}
/>
              </div>

              <div className="flex flex-col">
              <Field
  id="newPassword"
  name="newPassword"
  type={showPassword ? 'text' : 'password'}
  placeholder="*************"
  validate={composeValidators(required,validatePassword)}
  render={({ input, meta }) => {
    const showNewPasswordError = Boolean(meta?.error && (meta?.submitFailed || meta?.modified));

    return <div className="relative grid gap-2">
      <div className="flex items-center">
        <Label className="text-start text-[#0D215C] font-bold text-lg" htmlFor="password">New Password</Label>
      </div>
      <input
        {...input}
        type={showPassword ? 'text' : 'password'}
        value={newPassword}
        aria-invalid={showNewPasswordError ? 'true' : 'false'}
        aria-describedby={showNewPasswordError ? 'newPassword-error' : undefined}
        onChange={(e) => {
          input.onChange(e); // Update form state
          setNewPassword(e.target.value); // Also update password state
        }}
        placeholder="*************"
      className="bg-white pl-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0D215C] h-9 w-full rounded-md text-base border border-[#3a5dd1] border-1 manrope-fontCss"
      />
      {showNewPasswordError && (
        <p id="newPassword-error" className="text-red-600 text-sm text-left" role="alert" aria-live="assertive">&nbsp;{meta.error}</p>
      )}
      <button
        type="button"
        className="absolute h-11 top-10 right-0 flex items-center px-4 cursor-pointer rounded-md focus-visible:ring-2 focus-visible:ring-[#0D215C]"
        onClick={togglePasswordVisibility}
        aria-label={showPassword ? 'Hide new password' : 'Show new password'}
        aria-pressed={showPassword}
      >
        {showPassword ? (
          <EyeNoneIcon className="h-5 w-5 text-black" />
        ) : (
          <EyeOpenIcon className="h-5 w-5 text-black" />
        )}
      </button>
    </div>;
  }}
/>
              </div>

              <PasswordStrengthMeter password={newPassword} />

              <Button type="submit" className="w-full bg-[#0D215C] mt-4 focus-visible:ring-2 focus-visible:ring-[#0D215C] focus-visible:ring-offset-2">
                Change Password
              </Button>
            </form>
          )}
        />
        {/* <button type='button' onClick={onClose} className="absolute top-2 right-2 text-gray-600">
          close
        </button> */}
      </div>
    </div>
  );
};

export default ResetPasswordDialog;
